/** API per la risoluzione manuale del ticker quando il mapping OpenFIGI è ambiguo (F-040). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type MappingCandidate = {
  ticker: string;
  exchange: string | null;
  figi: string | null;
  currency: string | null;
  securityType: string | null;
};

export type InstrumentMapping = {
  instrumentId: string;
  symbol: string | null;
  providerSymbol: string | null;
  mappingStatus: string | null;
  marketMic: string | null;
  marketResolutionStatus: string;
};

/**
 * Il ticker di quotazione manca finché non è `resolved` **con** un simbolo:
 * `ambiguous` e `not_found` sono i due casi che il dialog di match esiste per
 * risolvere, e un `providerSymbol` assente con stato `resolved` sarebbe
 * comunque inservibile.
 *
 * Vive qui e non accanto ai componenti che la usano perché è una regola sul
 * dominio del mapping, non sulla resa: la stessa domanda se la pongono la
 * watchlist, la wishlist e la vista di dettaglio.
 */
export function needsTickerMatch(mapping: {
  providerSymbol: string | null;
  mappingStatus: string | null;
}): boolean {
  return !mapping.providerSymbol || mapping.mappingStatus !== 'resolved';
}

export const mappingKeys = {
  candidates: (instrumentId: string) => ['mapping', 'candidates', instrumentId] as const,
};

export function useMappingCandidates(instrumentId: string, enabled: boolean) {
  return useQuery({
    queryKey: mappingKeys.candidates(instrumentId),
    queryFn: ({ signal }) => api.get<{ items: MappingCandidate[] }>(`/instruments/${instrumentId}/mapping/candidates`, signal),
    enabled,
  });
}

export function useSetMapping(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticker: string) => api.patch<InstrumentMapping>(`/instruments/${instrumentId}/mapping`, { ticker }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['watchlist', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['markets', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['instruments'] });
    },
  });
}
